import { useState, useEffect } from "react";
import { Plus, Clock, AlertTriangle, Check, CheckCircle, Calendar, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Todo, Customer } from "@/types/database";
import { Link } from "react-router-dom";
import { useCustomerManagement } from "@/hooks/useCustomerManagement";

const TODO_TEMPLATES = [
  {
    type: "premium_cancel" as const,
    title: "Premium Kündigen",
    reminderDays: 54,
    description: "Premium-Abonnement kündigen"
  },
  {
    type: "credit_activation" as const,
    title: "Gutschrift Aktivierungsgebühr",
    reminderDays: 30,
    description: "Aktivierungsgebühr gutschreiben"
  },
  {
    type: "technician_order" as const,
    title: "Technikerauftrag",
    reminderDays: 1,
    description: "Technikertermin vereinbaren"
  },
  {
    type: "custom_cancel" as const,
    title: "Kündigen",
    reminderDays: 30,
    description: "Service kündigen"
  },
  {
    type: "custom" as const,
    title: "Benutzerdefiniert",
    reminderDays: 7,
    description: "Eigene Aufgabe"
  }
];

export const HomeTodos = () => {
  const { toast } = useToast();
  const { addProtocolEntry } = useCustomerManagement();
  const [todos, setTodos] = useState<Todo[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedTodo, setSelectedTodo] = useState<Todo | null>(null);
  const [isCompletingTodo, setIsCompletingTodo] = useState(false);
  const [completionComment, setCompletionComment] = useState("");
  const [newTodo, setNewTodo] = useState<{
    customerId: string;
    customerName: string;
    type: Todo['type'];
    customTitle: string;
    customDescription: string;
    customDays: number;
  }>({
    customerId: "",
    customerName: "",
    type: "premium_cancel",
    customTitle: "",
    customDescription: "",
    customDays: 7,
  });

  useEffect(() => {
    const setupRealtimeAndLoadData = async () => {
      const loadTodos = async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            setLoading(false);
            return;
          }

          const { data, error } = await supabase
            .from('todos')
            .select('*')
            .eq('completed', false)
            .order('due_date', { ascending: true })
            .limit(4);

          if (error) {
            console.error('Error loading todos:', error);
          } else {
            setTodos(data || []);
          }
        } catch (error) {
          console.error('Error:', error);
        } finally {
          setLoading(false);
        }
      };

      await loadTodos();
      loadCustomers();

      // Set up realtime subscription for todos
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const channel = supabase
          .channel('hometodos-changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'todos',
              filter: `user_id=eq.${session.user.id}`
            },
            (payload) => {
              console.log('HomeTodos change detected:', payload);
              // Reload todos when any change occurs
              loadTodos();
            }
          )
          .subscribe();

        return () => {
          supabase.removeChannel(channel);
        };
      }
    };

    setupRealtimeAndLoadData();
  }, []);

  const loadTodos = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('todos')
        .select('*')
        .eq('completed', false)
        .order('due_date', { ascending: true })
        .limit(4);

      if (error) {
        console.error('Error loading todos:', error);
      } else {
        setTodos(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase
        .from('customers')
        .select('id, first_name, last_name')
        .order('first_name', { ascending: true });

      if (error) {
        console.error('Error loading customers:', error);
      } else {
        const customersWithDisplayName = (data || []).map(customer => ({
          id: customer.id,
          first_name: customer.first_name,
          last_name: customer.last_name,
          name: `${customer.first_name} ${customer.last_name}`.trim(),
          user_id: '', // Add required fields with defaults
          created_at: '',
          updated_at: '',
          email: null,
          phone: null,
          address: null,
          notes: null
        }));
        setCustomers(customersWithDisplayName);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const calculateDueDate = (reminderDays: number): string => {
    const date = new Date();
    date.setDate(date.getDate() + reminderDays);
    return date.toISOString().split('T')[0];
  };

  const createTodo = async () => {
    if (!newTodo.customerId || !newTodo.customerName) {
      toast({
        title: "Fehler",
        description: "Bitte wählen Sie einen Kunden aus.",
        variant: "destructive",
      });
      return;
    }

    if (newTodo.type === "custom" && (!newTodo.customTitle || !newTodo.customDescription)) {
      toast({
        title: "Fehler",
        description: "Bitte füllen Sie Titel und Beschreibung für das benutzerdefinierte To-Do aus.",
        variant: "destructive",
      });
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({
        title: "Fehler",
        description: "Sie müssen angemeldet sein, um To-Dos zu erstellen.",
        variant: "destructive",
      });
      return;
    }

    const template = TODO_TEMPLATES.find(t => t.type === newTodo.type);
    if (!template) return;

    let title, description, reminderDays;

    if (newTodo.type === "custom") {
      title = newTodo.customTitle;
      description = newTodo.customDescription;
      reminderDays = newTodo.customDays;
    } else if (newTodo.type === "custom_cancel" && newTodo.customTitle) {
      title = `${newTodo.customTitle} Kündigen`;
      description = template.description;
      reminderDays = template.reminderDays;
    } else {
      title = template.title;
      description = template.description;
      reminderDays = template.reminderDays;
    }

    try {
      const { data, error } = await supabase
        .from('todos')
        .insert({
          customer_id: newTodo.customerId,
          title,
          description,
          due_date: calculateDueDate(reminderDays),
          priority: 'medium',
          user_id: session.user.id
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating todo:', error);
        toast({
          title: "Fehler",
          description: "To-Do konnte nicht erstellt werden.",
          variant: "destructive",
        });
        return;
      }

      setIsCreating(false);
      setNewTodo({
        customerId: "",
        customerName: "",
        type: "premium_cancel",
        customTitle: "",
        customDescription: "",
        customDays: 7,
      });
      loadTodos(); // Reload todos

      // Send email notification to customer
      try {
        await supabase.functions.invoke('send-task-notification', {
          body: { todoId: data.id, type: 'created' }
        });
      } catch (emailError) {
        console.error('Error sending task notification email:', emailError);
      }

      toast({
        title: "To-Do erstellt",
        description: `${data.title} wurde zur Liste hinzugefügt.`,
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
    }
  };

  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getPriorityColor = (daysUntil: number) => {
    if (daysUntil < 0) return "destructive";
    if (daysUntil <= 3) return "destructive";
    if (daysUntil <= 7) return "outline";
    return "secondary";
  };

  const getPriorityIcon = (daysUntil: number) => {
    if (daysUntil < 0) return AlertTriangle;
    if (daysUntil <= 3) return AlertTriangle;
    return Clock;
  };

  const completeTodo = async () => {
    if (!selectedTodo) return;
    
    setIsCompletingTodo(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Fehler",
          description: "Sie müssen angemeldet sein.",
          variant: "destructive",
        });
        return;
      }

      // Mark todo as completed
      const { error } = await supabase
        .from('todos')
        .update({
          completed: true,
          completed_at: new Date().toISOString(),
          completed_comment: completionComment
        })
        .eq('id', selectedTodo.id);

      if (error) {
        console.error('Error completing todo:', error);
        toast({
          title: "Fehler",
          description: "To-Do konnte nicht abgeschlossen werden.",
          variant: "destructive",
        });
        return;
      }

      // Add protocol entry to customer if customer_id exists
      if (selectedTodo.customer_id) {
        const protocolEntry = `Aufgabe abgeschlossen: ${selectedTodo.title}${completionComment ? ` - ${completionComment}` : ''}`;
        await addProtocolEntry(selectedTodo.customer_id, protocolEntry);
      }

      // Send email notification to customer
      try {
        await supabase.functions.invoke('send-task-notification', {
          body: { todoId: selectedTodo.id, type: 'completed' }
        });
      } catch (emailError) {
        console.error('Error sending task notification email:', emailError);
      }

      // Reset states and reload
      setSelectedTodo(null);
      setCompletionComment("");
      loadTodos();

      toast({
        title: "Aufgabe abgeschlossen",
        description: "Die Aufgabe wurde erfolgreich abgeschlossen und im Kundenprotokoll dokumentiert.",
      });

    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
    } finally {
      setIsCompletingTodo(false);
    }
  };

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unbekannter Kunde';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-muted-foreground">Lade To-Dos...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-primary/20">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="h-5 w-5" />
                Anstehende To-Dos
              </CardTitle>
              <CardDescription>
                Die nächsten 4 Aufgaben im Überblick
              </CardDescription>
            </div>
            
            <Dialog open={isCreating} onOpenChange={setIsCreating}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-2">
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Neue Aufgabe</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="mx-4 max-w-md md:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-lg">Neue Aufgabe erstellen</DialogTitle>
                  <DialogDescription className="text-sm">
                    Erstellen Sie eine neue Aufgabe für einen Kunden
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Kunde auswählen</Label>
                    <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={customerSearchOpen}
                          className="w-full justify-between"
                        >
                          {newTodo.customerName || "Kunde auswählen..."}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <Command>
                          <CommandInput placeholder="Kunde suchen..." />
                          <CommandList>
                            <CommandEmpty>Kein Kunde gefunden.</CommandEmpty>
                            <CommandGroup>
                              {customers.map((customer) => (
                                <CommandItem
                                  key={customer.id}
                                  value={customer.name}
                                  onSelect={() => {
                                    setNewTodo(prev => ({
                                      ...prev,
                                      customerId: customer.id,
                                      customerName: customer.name
                                    }));
                                    setCustomerSearchOpen(false);
                                  }}
                                >
                                  <Check className="mr-2 h-4 w-4 opacity-0" />
                                  {customer.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label>To-Do Typ</Label>
                    <Select 
                      value={newTodo.type} 
                      onValueChange={(value: Todo['type']) => setNewTodo(prev => ({ ...prev, type: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TODO_TEMPLATES.map((template) => (
                          <SelectItem key={template.type} value={template.type}>
                            {template.title} ({template.reminderDays} Tage)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {newTodo.type === "custom_cancel" && (
                    <div className="space-y-2">
                      <Label>Service Name</Label>
                      <Input
                        value={newTodo.customTitle}
                        onChange={(e) => setNewTodo(prev => ({ ...prev, customTitle: e.target.value }))}
                        placeholder="z.B. Mobilfunk, Internet, TV"
                      />
                    </div>
                  )}

                  {newTodo.type === "custom" && (
                    <>
                      <div className="space-y-2">
                        <Label>To-Do Titel</Label>
                        <Input
                          value={newTodo.customTitle}
                          onChange={(e) => setNewTodo(prev => ({ ...prev, customTitle: e.target.value }))}
                          placeholder="z.B. Nachfassen, Anrufen, E-Mail senden"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Beschreibung</Label>
                        <Textarea
                          value={newTodo.customDescription}
                          onChange={(e) => setNewTodo(prev => ({ ...prev, customDescription: e.target.value }))}
                          placeholder="Details zur Aufgabe..."
                          rows={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Erinnerung in Tagen</Label>
                        <Input
                          type="number"
                          min="1"
                          max="365"
                          value={newTodo.customDays}
                          onChange={(e) => setNewTodo(prev => ({ ...prev, customDays: parseInt(e.target.value) || 7 }))}
                        />
                      </div>
                    </>
                  )}

                  <div className="flex flex-col sm:flex-row justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsCreating(false)} size="sm">
                      Abbrechen
                    </Button>
                    <Button onClick={createTodo} size="sm">
                      To-Do erstellen
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        
        <CardContent className="min-w-0 max-w-full">
          {todos.length > 0 ? (
            <div className="space-y-3">
              {todos.map((todo) => {
                const daysUntil = getDaysUntilDue(todo.due_date || '');
                const PriorityIcon = getPriorityIcon(daysUntil);
                
                return (
                  <div 
                    key={todo.id} 
                    className="p-3 border rounded-lg hover:bg-muted/50 transition-colors min-w-0 cursor-pointer"
                    onClick={() => setSelectedTodo(todo)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <Badge variant={getPriorityColor(daysUntil)} className="text-xs">
                        {daysUntil < 0 ? `${Math.abs(daysUntil)} Tage überfällig` : 
                         daysUntil === 0 ? 'Heute' :
                         daysUntil === 1 ? 'Morgen' :
                         `${daysUntil} Tage`}
                      </Badge>
                      <div className="flex-1"></div>
                    </div>
                    
                    <div className="mb-2">
                      <p className="text-xs text-muted-foreground">
                        {todo.customer_id ? getCustomerName(todo.customer_id) : 'Kein Kunde zugeordnet'}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <PriorityIcon className={`h-4 w-4 flex-shrink-0 ${daysUntil < 0 ? 'text-destructive' : daysUntil <= 3 ? 'text-destructive' : 'text-muted-foreground'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{todo.title}</p>
                        {todo.description && (
                          <p className="text-xs text-muted-foreground truncate">{todo.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              
              <div className="pt-2 text-center">
                <Link to="/todos">
                  <Button variant="outline" size="sm">
                    Alle To-Dos anzeigen
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <Clock className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-3">Keine anstehenden To-Dos</p>
              <Dialog open={isCreating} onOpenChange={setIsCreating}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="gap-2">
                    <Plus className="h-4 w-4" />
                    Erste Aufgabe erstellen
                  </Button>
                </DialogTrigger>
              </Dialog>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Todo Detail Dialog */}
      <Dialog open={!!selectedTodo} onOpenChange={(open) => !open && setSelectedTodo(null)}>
        <DialogContent className="mx-0 w-[90vw] max-w-[320px] md:mx-4 md:max-w-lg">
          {selectedTodo && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Aufgabe Details
                </DialogTitle>
                <DialogDescription>
                  Vollständige Informationen zur Aufgabe
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium">Aufgabe</Label>
                    <p className="text-sm text-muted-foreground">{selectedTodo.title}</p>
                  </div>

                  {selectedTodo.description && (
                    <div>
                      <Label className="text-sm font-medium">Notiz</Label>
                      <p className="text-sm text-muted-foreground">{selectedTodo.description}</p>
                    </div>
                  )}

                  {selectedTodo.customer_id && (
                    <div>
                      <Label className="text-sm font-medium">Kunde</Label>
                      <Link to={`/customers?customerId=${selectedTodo.customer_id}`}>
                        <p className="text-sm text-muted-foreground hover:text-primary cursor-pointer underline">
                          {getCustomerName(selectedTodo.customer_id)}
                        </p>
                      </Link>
                    </div>
                  )}

                  {selectedTodo.due_date && (
                    <div>
                      <Label className="text-sm font-medium">Fälligkeitsdatum</Label>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <p className="text-sm text-muted-foreground">
                          {new Date(selectedTodo.due_date).toLocaleDateString('de-DE')}
                        </p>
                        <Badge variant={getPriorityColor(getDaysUntilDue(selectedTodo.due_date))} className="text-[10px] font-normal text-center px-1.5 py-0.5">
                          {(() => {
                            const daysUntil = getDaysUntilDue(selectedTodo.due_date);
                            return daysUntil < 0 ? `${Math.abs(daysUntil)} Tage überfällig` : 
                                   daysUntil === 0 ? 'Heute' :
                                   daysUntil === 1 ? 'Morgen' :
                                   `${daysUntil} Tage`;
                          })()}
                        </Badge>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="text-sm font-medium">Erstellt am</Label>
                    <p className="text-sm text-muted-foreground">
                      {new Date(selectedTodo.created_at).toLocaleDateString('de-DE', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label className="text-sm font-medium">Abschluss-Kommentar (optional)</Label>
                  <Textarea
                    value={completionComment}
                    onChange={(e) => setCompletionComment(e.target.value)}
                    placeholder="Notizen zum Abschluss der Aufgabe..."
                    rows={3}
                  />
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-2">
                  <Button variant="outline" onClick={() => setSelectedTodo(null)} size="sm" className="h-8 px-2 text-xs">
                    Schließen
                  </Button>
                  <Button 
                    onClick={completeTodo} 
                    disabled={isCompletingTodo}
                    size="sm"
                    className="gap-1 h-8 px-2 text-xs"
                  >
                    <CheckCircle className="h-3 w-3" />
                    {isCompletingTodo ? 'Wird abgeschlossen...' : 'Aufgabe abschließen'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};